var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Module } from "../module";
import { localizationService } from "../localization";
import { renderManager } from "./RenderManager";
import { renderer } from "../rendering/RendererGlobals";
import { BoundedReferenceSpaceMessage, EnableWebXRMessage, WebXREnabledMessage, WebXRPresentingMessage } from "../../messages/xr/WebXRMessages";
import { camera } from "../rendering/camera";
import { animationFrame$ } from "../../messages/xr/AnimationFrame";
import { gamepadDetectionService, webXRControllerSessionBridge } from "../inputs/controller";
import { Vector3 } from "flavr-math";
import { vrService } from "./VRService";
import { localPlayerContainer } from "../localplayer/LocalPlayerContainer";
import { capabilitiesService } from "../rendering/CapabilitiesService";
import { AskOkMessage, convertToNumber3, promiseWithTimeout } from "flavr-common";
const tmpTrackingAreaSize = new Vector3();
const tmpTrackingAreaMax = new Vector3();
const tmpTrackingAreaMin = new Vector3();
const VR_FRAMEBUFFER_SCALE = 1.0;
/**
 * Integrates immersive WebXR session management so the client can enter or leave VR, update tracking-space data and keep other modules informed about XR presentation state. As sessions start, end or update, it broadcasts `WebXREnabledMessage`, `WebXRPresentingMessage` and `BoundedReferenceSpaceMessage` so other modules stay in sync.
 *
 * Messages:
 * - `EnableWebXRMessage` - Starts or ends an immersive XR session, either as a normal bus message or as an ask request that expects completion feedback.
 */
export class WebXRModule extends Module {
    constructor() {
        super("WebXRModule");
        this.xrManager = renderer.xr;
        this.pendingEnableVR = Promise.resolve();
        this.desiredEnabled = false;
        this.webXRToggleButton = null;
        this.positionBeforeSwitchingVRMode = new Vector3();
        this.directionBeforeSwitchingVRMode = new Vector3();
        this.togglePresenting = (event) => __awaiter(this, void 0, void 0, function* () {
            event.preventDefault();
            try {
                yield this.enableVR(!renderManager.isXRPresenting());
            }
            catch (error) {
                if (this.webXRToggleButton !== null) {
                    const children = this.webXRToggleButton.getElementsByTagName("span");
                    if (children.length === 1 && children.item(0) !== null) {
                        const child = children.item(0);
                        if (child !== null)
                            child.textContent = JSON.stringify(error);
                    }
                    this.webXRToggleButton.style.backgroundImage = "url('icons/vr_notsupported.png')";
                }
            }
        });
        this.onSessionStarted = (session) => __awaiter(this, void 0, void 0, function* () {
            vrService.checkIsEmulatedXRDevice(session);
            session.addEventListener("end", this.onSessionEnded);
            webXRControllerSessionBridge.onSessionStarted(this.xrManager, session);
            // Keep the headset's native resolution to avoid the quadratic GPU cost of supersampling.
            if (this.xrManager.isPresenting) {
                console.warn("[xr] WebXRManager is still presenting - cannot change framebuffer scale");
            }
            else {
                this.xrManager.setFramebufferScaleFactor(VR_FRAMEBUFFER_SCALE);
            }
            // Foveation auf 0 = volle Auflösung überall im Sichtfeld; Standard ist 1.0 (maximale
            // Foveation = Bildecken werden deutlich niedriger aufgelöst → sichtbares Aliasing).
            // Nur auf High-End-GPUs komplett deaktivieren; auf Mid-Range leicht reduzieren (0.5).
            const foveation = capabilitiesService.isMobileVR ? 1.0 : capabilitiesService.isHighEndGPU ? 0 : 0.5;
            this.xrManager.setFoveation(foveation);
            console.info(`[xr] VR Anti-Aliasing: Framebuffer-Skalierungsfaktor ${VR_FRAMEBUFFER_SCALE}, Foveation ${foveation}`);
            yield this.xrManager.setSession(session);
            this.currentSession = session;
            session.addEventListener("inputsourceschange", this.onInputSourcesChange);
            try {
                const referenceSpace = yield this.currentSession.requestReferenceSpace("bounded-floor");
                this.onRequestBoundedReferenceSpace(referenceSpace);
            }
            catch (error) {
                this.resetBoundedReferenceSpace();
            }
            this.positionBeforeSwitchingVRMode.copy(camera.perspectiveCam.getWorldPosition(new Vector3()));
            this.directionBeforeSwitchingVRMode = localPlayerContainer.cameraWorldViewDirection;
            this.connection.send(new WebXRPresentingMessage(true, convertToNumber3(this.positionBeforeSwitchingVRMode), convertToNumber3(this.directionBeforeSwitchingVRMode)));
        });
        this.onSessionEnded = () => {
            if (this.currentSession !== undefined) {
                this.currentSession.removeEventListener("end", this.onSessionEnded);
                this.currentSession.removeEventListener("inputsourceschange", this.onInputSourcesChange);
                this.currentSession = undefined;
            }
            // Reset framebuffer scale factor and foveation for subsequent sessions.
            // Deferred because this "end" listener is registered before the one from three.js and therefore
            // fires first - at that point isPresenting is still true and setFramebufferScaleFactor() would be ignored.
            queueMicrotask(() => {
                if (this.xrManager.isPresenting)
                    return;
                this.xrManager.setFramebufferScaleFactor(1.0);
                this.xrManager.setFoveation(1.0);
                console.info("[xr] VR anti-aliasing: framebuffer scale factor and foveation reset");
            });
            webXRControllerSessionBridge.onSessionEnded(this.xrManager);
            gamepadDetectionService.clearGamepads();
            this.positionBeforeSwitchingVRMode.copy(localPlayerContainer.getPosition());
            this.directionBeforeSwitchingVRMode = localPlayerContainer.cameraWorldViewDirection;
            this.connection.send(new WebXRPresentingMessage(false, convertToNumber3(this.positionBeforeSwitchingVRMode), convertToNumber3(this.directionBeforeSwitchingVRMode)));
            this.resetBoundedReferenceSpace();
        };
        /**
         * Computes the bounded-floor tracking area's size and min/max extents from the reference space's bounds
         * geometry and broadcasts them via `BoundedReferenceSpaceMessage`.
         */
        this.onRequestBoundedReferenceSpace = (value /* XRReferenceSpace */) => {
            const boundedValue = value;
            tmpTrackingAreaSize.setScalar(0);
            tmpTrackingAreaMin.setScalar(0);
            tmpTrackingAreaMax.setScalar(0);
            if (boundedValue.boundsGeometry.length >= 2) {
                tmpTrackingAreaMin.copy(boundedValue.boundsGeometry[0]);
                tmpTrackingAreaMax.copy(boundedValue.boundsGeometry[0]);
                for (let i = 1, len = boundedValue.boundsGeometry.length; i < len; ++i) {
                    tmpTrackingAreaMin.min(boundedValue.boundsGeometry[i]);
                    tmpTrackingAreaMax.max(boundedValue.boundsGeometry[i]);
                }
                tmpTrackingAreaSize.subVectors(tmpTrackingAreaMax, tmpTrackingAreaMin);
            }
            this.connection.send(new BoundedReferenceSpaceMessage(convertToNumber3(tmpTrackingAreaSize), convertToNumber3(tmpTrackingAreaMin), convertToNumber3(tmpTrackingAreaMax)));
        };
        this.onInputSourcesChange = (event) => {
            gamepadDetectionService.updateInputSources(event);
        };
        this.onInputUpdate = (xrFrame) => {
            if (xrFrame === undefined) {
                return;
            }
            const referenceSpace = this.xrManager.getReferenceSpace();
            if (referenceSpace == null)
                return;
            const pose = xrFrame.getViewerPose(referenceSpace);
            if (pose == null)
                return;
            // assume the orientation is the same for all views
            camera.perspectiveCam.quaternion.copy(pose.views[0].transform.orientation);
            // calculate the camera position a the center of all view positions.
            camera.perspectiveCam.position.set(0, 0, 0);
            for (const view of pose.views) {
                camera.perspectiveCam.position.add(view.transform.position);
            }
            if (pose.views.length > 0)
                camera.perspectiveCam.position.divideScalar(pose.views.length);
            camera.perspectiveCam.updateMatrix();
        };
        this.handleEnableWebXRMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            let success = true;
            try {
                yield this.enableVR(msg.enable);
            }
            catch (e) {
                console.warn("xr", e);
                success = false;
            }
            if (askId !== undefined) {
                this.connection.answer(askId, new AskOkMessage(success));
            }
        });
    }
    /**
     * Probes for WebXR immersive-VR support and, if available, wires up the VR toggle button and per-frame input
     * polling, then reports the support status to `vrService`. Subscribes to `EnableWebXRMessage` regardless of
     * whether the probe succeeds.
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.connection.subscribeByTypeOrAskRequest(EnableWebXRMessage, this.handleEnableWebXRMessage);
            try {
                yield this.initWebXR();
                if (this.webXRToggleButton !== null) {
                    this.webXRToggleButton.addEventListener("click", (event) => { void this.togglePresenting(event); });
                    this.webXRToggleButton.disabled = false;
                    const children = this.webXRToggleButton.getElementsByTagName("span");
                    if (children.length === 1 && children.item(0) !== null) {
                        const child = children.item(0);
                        if (child !== null)
                            child.textContent = localizationService.getTranslation({
                                key: "ui.vr.start"
                            });
                    }
                }
                vrService.changeVRSupported(true);
                animationFrame$.onInputUpdate$.add(this.onInputUpdate);
            }
            catch (error) {
                console.debug("[xr]", error);
                vrService.changeVRSupported(false);
                if (this.webXRToggleButton !== null) {
                    const children = this.webXRToggleButton.getElementsByTagName("span");
                    if (children.length === 1 && children.item(0) !== null) {
                        const child = children.item(0);
                        if (child !== null)
                            child.textContent = JSON.stringify(error);
                    }
                    this.webXRToggleButton.style.backgroundImage = "url('icons/vr_notsupported.png')";
                }
            }
        });
    }
    /**
     * Serializes enable/disable requests. Without this, two concurrent requests could both pass the
     * `currentSession` check and call `requestSession()` twice, which fails with InvalidStateError.
     */
    enableVR(enabled) {
        const run = this.pendingEnableVR.then(() => __awaiter(this, void 0, void 0, function* () { yield this.enableVRInternal(enabled); }));
        this.pendingEnableVR = run.catch(() => { });
        return run;
    }
    enableVRInternal(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            this.desiredEnabled = enabled;
            // no-op fast path
            if (enabled === (this.currentSession !== undefined))
                return;
            if (navigator.xr === undefined) {
                console.warn("[xr] navigator.xr is not defined");
                return;
            }
            if (!(yield this.isVRAvailable())) {
                console.warn("[xr] vr is not available");
                return;
            }
            this.xrManager.enabled = enabled;
            this.connection.send(new WebXREnabledMessage(enabled));
            if (enabled) {
                const sessionInit = {
                    optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking"]
                };
                // A previous request is still pending (e.g. runtime waiting for the headset) - don't issue a
                // second one, it would fail with InvalidStateError. The pending request will be adopted.
                if (this.pendingSessionRequest !== undefined)
                    return;
                const request = navigator.xr.requestSession("immersive-vr", sessionInit);
                this.pendingSessionRequest = request;
                // Handles the session whenever it is granted - also after the timeout below expired.
                const handled = request.then((session) => __awaiter(this, void 0, void 0, function* () {
                    this.pendingSessionRequest = undefined;
                    if (this.desiredEnabled && this.currentSession === undefined) {
                        yield this.onSessionStarted(session);
                    }
                    else {
                        yield session.end().catch(() => { });
                        this.xrManager.enabled = false;
                        this.connection.send(new WebXREnabledMessage(false));
                    }
                }), (e) => {
                    this.pendingSessionRequest = undefined;
                    this.xrManager.enabled = false;
                    this.connection.send(new WebXREnabledMessage(false));
                    throw e;
                });
                try {
                    yield promiseWithTimeout(handled, 10000);
                }
                catch (e) {
                    if (this.pendingSessionRequest !== undefined) {
                        console.warn("[xr] XR session not granted yet (runtime/headset not ready?), still waiting in background");
                    }
                    else {
                        console.warn("[xr]", e);
                    }
                }
            }
            else {
                if (this.currentSession !== undefined) {
                    yield this.currentSession.end();
                }
            }
        });
    }
    isVRAvailable() {
        return __awaiter(this, void 0, void 0, function* () {
            const xr = navigator.xr;
            if (xr === undefined)
                return false;
            try {
                return yield xr.isSessionSupported("immersive-vr");
            }
            catch (_a) {
                return false;
            }
        });
    }
    initWebXR() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!("xr" in navigator)) {
                const errorMessageKey = !window.isSecureContext ? "ui.xr.requiresHttps" : "ui.xr.notSupported";
                yield Promise.reject(localizationService.getTranslation({ key: errorMessageKey }));
                return;
            }
            const supported = yield navigator.xr /* as XR */
                .isSessionSupported("immersive-vr");
            if (!supported) {
                yield Promise.reject(localizationService.getTranslation({
                    key: "ui.xr.noImmersiveSession"
                }));
                return;
            }
            yield Promise.resolve();
        });
    }
    resetBoundedReferenceSpace() {
        this.connection.send(new BoundedReferenceSpaceMessage([0, 0, 0], [0, 0, 0], [0, 0, 0]));
    }
}
